<script lang="ts">
    
    import { LngLat, Map as Mapbox, Marker } from "mapbox-gl";
    import { getContext, onDestroy, onMount } from "svelte";
    import type { CoordinatesDto, Fn } from "../../global";
    import { calcBearing, override, playerPosition } from "../../Services/geolocation";
    import { getPlayer } from "../../Services/playerRepository";
    import Sprite from "../Shared/Sprite.svelte";

    const getMapbox = getContext('getMapbox') as () => Mapbox;

    let marker: Marker;
    let unsubPlayerPosition: Fn;
    let sprite: Sprite;
    let stopAnimationTimeout: number;
    let prevPosition: LngLat;
    let map: Mapbox;

    onMount(() => {

        map = getMapbox();

        unsubPlayerPosition = playerPosition.subscribe(position => {
            if (!position) {
                return;
            }
            marker
                ? updateMarker(position)
                : createMarker(position);
        });

        map.on('move', onMapMove);

    });
    
    onDestroy(() => {
        marker?.remove();
        unsubPlayerPosition?.();
        map?.off('move', onMapMove);
    });

    function onMapMove(): void {
        updateZIndex();
    }

    function updateZIndex(): void {
        if (!marker) {
            return;
        }
        const lngLat = marker.getLngLat();
        const { y } = map.project(lngLat);
        marker.getElement().style.setProperty('z-index', `${Math.floor(y)}`);
    }

    function createMarker(position: CoordinatesDto): void {
        const element = document.createElement('div') as HTMLElement;
        element.classList.add('player-marker');

        const player = getPlayer();
        const draggable = player?.name === 'drag';

        element.classList.toggle('draggable', draggable);

        marker = new Marker({
            element,
            anchor: 'bottom',
            draggable,
        });

        marker.on('dragstart', () => prevPosition = marker.getLngLat());

        marker.on('dragend', () => {
            const { lng, lat } = marker.getLngLat();
            override({ lon: lng, lat: lat })
        });

        marker.setLngLat(position);
        marker.addTo(map);

        updateZIndex();

        sprite = new Sprite({
            target: element,
            props: {
                src: player.sprite,
                animation: 'walk_se',
                play: false
            }
        });
    }

    function updateMarker(position: CoordinatesDto): void {
        const { lng: lon, lat } = prevPosition || marker.getLngLat();
        
        marker.setLngLat(position);
        
        const bearing = calcBearing({ lon, lat }, position); 

        let animationName: string;

        if (bearing < 90) {
            animationName = 'walk_ne';
        } else if (bearing < 180) {
            animationName = 'walk_se';
        } else if (bearing < 270) {
            animationName = 'walk_sw';
        } else  {
            animationName = 'walk_nw';
        }

        playAnimation(animationName);
        updateZIndex();
    }

    function playAnimation(animationName: string): void {
        if (!sprite) {
            return;
        }
        window.clearTimeout(stopAnimationTimeout);
        stopAnimationTimeout = window.setTimeout(stopAnimation, 5000);
        sprite.setupAnimation(animationName);
        sprite.playSprite();
    }

    function stopAnimation(): void {
        if (!sprite) {
            return;
        }
        sprite.stopSprite();
    }

</script>

<style lang="scss">

    :global(.player-marker) {
        z-index: 2;
        width: 50px;
        height: 60px;

        &:not(.draggable) {
            pointer-events: none;
            touch-action: none;
        }
    }

</style>
